import React from 'react';
import { CreditCard as Edit3 } from 'lucide-react';
import { formatCurrencyPrecise } from '../../utils/formatting';
import { calculateAdjustedPrimeAmount } from '../../utils/primeCalculations';
import { PRIME_CALCULATION } from '../../constants/primes';
import { PrimeIndemnite } from '../../types';

interface PrimeHeaderProps {
  prime: PrimeIndemnite;
  workTimePercentage: number;
  selectedStatus?: StatusType;
  onToggle: () => void;
  isEditing?: boolean;
  onEdit?: () => void;
  onToggleDetailsClick?: () => void;
}

export default function PrimeHeader({
  prime,
  workTimePercentage,
  selectedStatus,
  onToggle,
  isEditing = false,
  onEdit,
  onToggleDetailsClick
}: PrimeHeaderProps) {
  const Icon = prime.icon;
  const adjustedAmount = calculateAdjustedPrimeAmount(prime, workTimePercentage);

  const getColorClasses = (active: boolean) => {
    if (active) {
      return 'bg-white border-green-400 shadow-md';
    } else {
      return 'bg-gray-50 border-gray-200';
    }
  };

  const getIconColorClass = (active: boolean) => {
    if (active) {
      return 'bg-green-100 text-green-700';
    } else {
      return 'bg-gray-100 text-gray-600';
    }
  };

  const getTextColorClass = (active: boolean) => {
    if (active) {
      return 'text-gray-900';
    } else {
      return 'text-gray-700';
    }
  };

  const getDescriptionColorClass = (active: boolean) => {
    if (active) {
      return 'text-gray-600';
    } else {
      return 'text-gray-500';
    }
  };

  const shouldShowMaxDisplay = () => {
    return (prime.id === 'autres' && 
           (!prime.active || prime.montantUtilisateur === PRIME_CALCULATION.SERVICE_MAX_AMOUNT)) ||
           (prime.id === 'service' && prime.isMaxCalculated && prime.montantDefaut > 0 && prime.montantUtilisateur === prime.montantDefaut);
  };

  const isEditable = onEdit && prime.type === 'variable' && (!prime.calculable || prime.isMaxCalculated);
  const displayAmount = shouldShowMaxDisplay()
    ? formatCurrencyPrecise(
        prime.id === 'service' && prime.isMaxCalculated ? prime.montantDefaut : PRIME_CALCULATION.SERVICE_MAX_AMOUNT
      )
    : formatCurrencyPrecise(adjustedAmount);

  return (
    <div
      onClick={onToggleDetailsClick}
      className="w-full flex items-start gap-2.5 mb-2 sm:mb-3 text-left hover:bg-gray-50 p-1 sm:p-2 rounded-lg transition-colors cursor-pointer"
    >
      <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 mt-0.5 ${getIconColorClass(prime.active)}`}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
      </div>

      <div className="flex-1 min-w-0 space-y-0.5">
        <h3 className={`font-semibold text-sm leading-snug line-clamp-2 ${getTextColorClass(prime.active)}`}>
          <span id={`prime-${prime.id}-title`}>{prime.nom}</span>
        </h3>
        {prime.aNegocier && selectedStatus === 'contractuel' && (
          <div>
            <span className="px-1.5 py-0.5 bg-orange-100 text-orange-800 text-[10px] rounded-full font-medium">
              A négocier
            </span>
          </div>
        )}
        <p className={`text-xs leading-snug ${getDescriptionColorClass(prime.active)} line-clamp-1 sm:line-clamp-2`}>
          <span id={`prime-${prime.id}-description`}>{prime.description}</span>
        </p>
        <div className="flex items-baseline gap-1 pt-0.5">
          {shouldShowMaxDisplay() && (
            <span className="text-[10px] text-gray-500 leading-none">jusqu'à</span>
          )}
          <div
            onClick={
              onEdit
                ? (e) => {
                    e.stopPropagation();
                    onEdit();
                  }
                : undefined
            }
            className={`text-base font-bold leading-tight ${
              prime.active ? 'text-gray-900' : 'text-gray-700'
            } ${isEditable ? 'cursor-pointer hover:bg-gray-100 rounded px-1 -mx-1' : ''}`}
            title={isEditable ? 'Cliquer pour modifier' : undefined}
          >
            {displayAmount}
            <span className="text-[10px] font-normal text-gray-500 ml-0.5">/mois</span>
            {isEditable && <Edit3 className="w-3 h-3 inline ml-1 opacity-50" />}
          </div>
          {workTimePercentage !== 100 && prime.active && !shouldShowMaxDisplay() && (
            <span className="text-[10px] text-gray-600 leading-none ml-1">
              {workTimePercentage}% TP
            </span>
          )}
        </div>
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-label={`${prime.active ? 'Désactiver' : 'Activer'} ${prime.nom}`}
        aria-checked={prime.active}
        role="switch"
        className={`relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex-shrink-0 mt-1 ${
          prime.active ? 'bg-green-600' : 'bg-gray-300'
        }`}
      >
        <span className="sr-only">
          {prime.active ? 'Désactiver' : 'Activer'} {prime.nom}
        </span>
        <span
          className={`inline-block h-3.5 w-3.5 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform ${
            prime.active ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}